/**
 * FieldSelectionList Component
 *
 * Reusable component for selecting fields to import.
 * Features:
 * - Checkbox per field with name, type, description
 * - Select All / Select None buttons
 * - Conflict indicator for fields that will overwrite existing
 */

import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { SchemaField, FIELD_TYPES } from '@/services/api/schemaApi';
import { ParsedFieldGroups } from './import-sources/JsonImportSource';
import { cn } from '@/lib/utils';

interface FieldSelectionListProps {
  fields: SchemaField[];
  selectedIds: Set<string>;
  existingFieldNames: string[];
  onSelectionChange: (selectedIds: Set<string>) => void;
  /** Optional per-field grouping. When provided, fields are rendered in sections. */
  groups?: ParsedFieldGroups;
}

export const FieldSelectionList: React.FC<FieldSelectionListProps> = ({
  fields,
  selectedIds,
  existingFieldNames,
  onSelectionChange,
  groups,
}) => {
  // Build display groups. When `groups` is provided, group fields by their
  // labels in the supplied order. Otherwise render a single anonymous group.
  const hasGroups = !!groups && groups.groupOrder.length > 1;
  const displayGroups: Array<{ label: string; fields: SchemaField[] }> = (() => {
    if (!groups || !hasGroups) {
      return [{ label: '', fields }];
    }
    const byLabel = new Map<string, SchemaField[]>();
    for (const f of fields) {
      const label = groups.groupByFieldId[f.id] ?? '';
      if (!byLabel.has(label)) byLabel.set(label, []);
      byLabel.get(label)!.push(f);
    }
    return groups.groupOrder
      .filter((label) => byLabel.has(label))
      .map((label) => ({ label, fields: byLabel.get(label)! }));
  })();

  // Toggle every field in a group
  const handleGroupToggle = (groupFields: SchemaField[]) => {
    const allSelected = groupFields.every((f) => selectedIds.has(f.id));
    const next = new Set(selectedIds);
    if (allSelected) {
      groupFields.forEach((f) => next.delete(f.id));
    } else {
      groupFields.forEach((f) => next.add(f.id));
    }
    onSelectionChange(next);
  };

  // Get fields that will cause conflicts
  const conflictingFields = fields.filter(
    (f) => selectedIds.has(f.id) && existingFieldNames.includes(f.name)
  );

  // Handle individual field toggle
  const handleToggle = (fieldId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(fieldId)) {
      newSelected.delete(fieldId);
    } else {
      newSelected.add(fieldId);
    }
    onSelectionChange(newSelected);
  };

  // Select all fields
  const handleSelectAll = () => {
    onSelectionChange(new Set(fields.map((f) => f.id)));
  };

  // Deselect all fields
  const handleSelectNone = () => {
    onSelectionChange(new Set());
  };

  // Get type label
  const getTypeLabel = (type: string) => {
    const typeConfig = FIELD_TYPES.find((t) => t.value === type);
    return typeConfig?.label || type;
  };

  if (fields.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No fields available. Parse JSON or load a schema first.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with select buttons */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          Available Fields ({fields.length})
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSelectAll}
            className="text-xs h-7"
          >
            Select All
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSelectNone}
            className="text-xs h-7"
          >
            Select None
          </Button>
        </div>
      </div>

      {/* Field list (grouped when groups metadata is provided) */}
      <div className="border rounded-lg divide-y max-h-[280px] overflow-y-auto">
        {displayGroups.map((group, gIdx) => {
          const groupSelectedCount = group.fields.filter((f) => selectedIds.has(f.id)).length;
          const allSelected = groupSelectedCount === group.fields.length;
          const noneSelected = groupSelectedCount === 0;
          const sectionTitle = group.label === '' ? 'From top level' : `From ${group.label}`;

          return (
            <div key={`group-${gIdx}-${group.label}`}>
              {hasGroups && (
                <label className="flex items-center gap-3 px-3 py-2 bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors">
                  <Checkbox
                    checked={allSelected ? true : noneSelected ? false : 'indeterminate'}
                    onCheckedChange={() => handleGroupToggle(group.fields)}
                  />
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {sectionTitle}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({groupSelectedCount}/{group.fields.length})
                  </span>
                </label>
              )}
              {group.fields.map((field) => {
                const isSelected = selectedIds.has(field.id);
                const isConflict = existingFieldNames.includes(field.name);

                return (
                  <label
                    key={field.id}
                    className={cn(
                      'flex items-center gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors',
                      isSelected && 'bg-accent/30',
                      hasGroups && 'pl-8'
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggle(field.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">
                          {field.name}
                        </span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {getTypeLabel(field.type)}
                        </span>
                        {field.required && (
                          <span className="text-xs text-red-500">Required</span>
                        )}
                        {isConflict && (
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertTriangle className="h-3 w-3" />
                            overwrites
                          </span>
                        )}
                      </div>
                      {field.description && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {field.description}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Conflict warning */}
      {conflictingFields.length > 0 && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400 text-xs">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            {conflictingFields.length} field{conflictingFields.length !== 1 ? 's' : ''} will
            overwrite existing: {conflictingFields.map((f) => f.name).join(', ')}
          </span>
        </div>
      )}
    </div>
  );
};

export default FieldSelectionList;
